import { TDynamicVariable } from "@shared/types";
import { CoreConfig } from "../types";
import { getLength, getComputedLines } from "../utils";

const getExtraPosts = (config: CoreConfig) => {
  const segmentLengths = config.Shapes.filter(
    (l) => l.type === "LINE" || (l.type as string) === "3_POINT_CURVE"
  ).map((l) => {
    return getLength(l);
  });
  const extraCornerPosts = config.cornerPostDistanceOnCenter
    ? segmentLengths
        .map((l) => {
          return Math.ceil(l / (config.cornerPostDistanceOnCenter || 1)) - 1;
        })
        .reduce((a, b) => a + b, 0)
    : 0;
  const extraLinePosts = config.linePostDistanceOnCenter
    ? segmentLengths
        .map((l) => {
          return Math.ceil(l / (config.linePostDistanceOnCenter || 1)) - 1;
        })
        .reduce((a, b) => a + b, 0)
    : 0;
  return {
    extraCornerPosts,
    extraLinePosts,
  };
};

export const calculatePoint = (
  variable: TDynamicVariable,
  config: CoreConfig
) => {
  // Get all middle point IDs from 3_POINT_CURVE shapes
  const middlePointIds = new Set(
    config.Shapes
      .filter((l) => (l.type as string) === "3_POINT_CURVE")
      .map((l) => (l.config as any)?.middle?.link)
      .filter(Boolean)
  );

  const points = config.Shapes.filter(
    (l) => l.type === "POINT" || !l.type
  ).filter((l) => {
    // Always exclude middle points from 3-point curves
    if (middlePointIds.has(l._id)) {
      return false;
    }
    if (!variable.subtype) {
      return true;
    }
    return variable.subtype.includes(l.config.posttype || "LINE_POST");
  });
  switch (variable.output) {
    case "QUANTITY":
    case "SEGMENT": {
      const { extraCornerPosts, extraLinePosts } = getExtraPosts(config);
      return (
        points.length +
        (variable.subtype?.includes("LINE_POST")
          ? extraLinePosts - extraCornerPosts
          : 0) +
        (variable.subtype?.includes("CORNER_POST") ? extraCornerPosts : 0)
      );
    }
    case "EXISTS":
      return points.length >= 1 ? 1 : 0;
    // @ts-ignore - INTERSECTIONS renamed to HOOKUP
    case "INTERSECTIONS":
    case "HOOKUP": {
      const { extraCornerPosts, extraLinePosts } = getExtraPosts(config);
      
      const lineLinks = config.Shapes.filter(
        (l) => l.type === "LINE" || !l.type || (l.type as string) === "3_POINT_CURVE"
      )
        .map((l) => {
          const links: string[] = [];
          if (l.config.posttype === "LINE_POST") return links;
          
          // Handle regular LINE shapes
          if (l.type === "LINE" || !l.type) {
            // Only add links that are NOT middle points
            if (l.config?.start?.link && !middlePointIds.has(l.config.start.link)) {
              links.push(l.config.start.link);
            }
            if (l.config?.end?.link && !middlePointIds.has(l.config.end.link)) {
              links.push(l.config.end.link);
            }
          }
          
          // Handle 3_POINT_CURVE shapes
          if ((l.type as string) === "3_POINT_CURVE") {
            // Only add start/end links that are NOT middle points
            if (l.config?.start?.link && !middlePointIds.has(l.config.start.link)) {
              links.push(l.config.start.link);
            }
            // Middle point is NEVER included in hookup calculations
            // It exists solely for length calculation
            if (l.config?.end?.link && !middlePointIds.has(l.config.end.link)) {
              links.push(l.config.end.link);
            }
          }
          
          return links;
        })
        .reduce((a, b) => [...a, ...b], []);

      return (
        points
          .map((line) => lineLinks.filter((link) => link === line._id).length)
          .reduce((a, b) => a + b, 0) +
        (variable?.subtype?.includes("CORNER_POST")
          ? extraCornerPosts * 2
          : 0) +
        (variable?.subtype?.includes("LINE_POST") ? extraLinePosts * 2 : 0)
      );
    }
  }
};
